/**
 * Color Builder Popup Component
 * Handles 2-step text color builder: text input and color selection per character
 */

const COLOR_BUILDER_VARIANT_GID_PREFIX = 'gid://shopify/ProductVariant/';

function colorBuilderVariantGid(variantId) {
  return COLOR_BUILDER_VARIANT_GID_PREFIX + String(variantId);
}

function colorBuilderMoneyDecimalFromCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '0.00';
  return (n / 100).toFixed(2);
}

function colorBuilderDebounce(fn, wait) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function colorBuilderSnapshotFromCharacterLines(characterLines) {
  const components = [];
  let totalCents = 0;
  for (const line of characterLines) {
    const q = Math.max(0, parseInt(String(line.quantity), 10) || 0);
    const unitCents = Number(line.price) || 0;
    const lineTotal = Number(line.line_price);
    const lineCents = Number.isFinite(lineTotal) ? lineTotal : unitCents * q;
    totalCents += lineCents;
    components.push({
      variant_id: colorBuilderVariantGid(line.variant_id),
      quantity: q,
      price: colorBuilderMoneyDecimalFromCents(unitCents),
    });
  }
  return {
    bundlePrice: colorBuilderMoneyDecimalFromCents(totalCents),
    bundleComponents: JSON.stringify(components),
  };
}

function colorBuilderGroupBundleCartItems(items) {
  const groups = new Map();
  for (const item of items) {
    const props = item.properties || {};
    const groupId = props._bundle_group;
    if (!groupId || typeof groupId !== 'string') continue;
    if (!groups.has(groupId)) {
      groups.set(groupId, { parent: null, characters: [] });
    }
    const bucket = groups.get(groupId);
    if (props._is_color_builder_child === 'true') {
      bucket.characters.push(item);
    } else if (props._is_color_builder_additional !== 'true') {
      // MFS-373: the parent bundle line is the one grouped by `_bundle_group` that is neither a
      // colour child nor an additional product. We no longer key off `_bundle_id` (removed so the
      // Engine cart transform stays dormant — see confirmBuilder).
      if (!bucket.parent) {
        bucket.parent = item;
      }
    }
  }
  return [...groups.values()].filter((b) => b.parent);
}

async function colorBuilderSyncBundleLineProperties() {
  if (!window.Shopify || !window.Shopify.routes || !window.Shopify.routes.root) return;
  let cart;
  try {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`, { credentials: 'same-origin' });
    if (!res.ok) return;
    cart = await res.json();
  } catch (e) {
    return;
  }
  const items = cart.items || [];
  const buckets = colorBuilderGroupBundleCartItems(items);
  const root = window.Shopify.routes.root;
  for (const bucket of buckets) {
    const parent = bucket.parent;
    const snapshot = colorBuilderSnapshotFromCharacterLines(bucket.characters);
    const prevProps = parent.properties || {};
    if (
      String(prevProps._bundle_price || '') === snapshot.bundlePrice &&
      String(prevProps._bundle_components || '') === snapshot.bundleComponents
    ) {
      continue;
    }
    const merged = { ...prevProps, _bundle_price: snapshot.bundlePrice, _bundle_components: snapshot.bundleComponents };
    try {
      const changeRes = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: parent.key,
          quantity: parent.quantity,
          properties: merged,
        }),
      });
      if (!changeRes.ok) continue;
      const body = await changeRes.json();
      if (body.status === 422) continue;
    } catch (e) {
      /* ignore */
    }
  }
}

if (!window.Eurus.loadedScript.has('color-builder-popup.js')) {
  window.Eurus.loadedScript.add('color-builder-popup.js');

  if (!window.__colorBuilderBundleCartListeners) {
    window.__colorBuilderBundleCartListeners = true;
    const runSync = colorBuilderDebounce(() => {
      void colorBuilderSyncBundleLineProperties();
    }, 120);
    document.addEventListener('eurus:cart:items-changed', runSync);
    document.addEventListener('cart:refresh', runSync);
  }

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('colorBuilderPopup', (config) => {
        return {
          showPopup: false,
          step: 1,
          text: '',
          maxLength: 20,
          minLength: 1,
          availableColors: [],
          // MFS-479: character index -> index into availableColors. Selection is keyed by the
          // swatch's position, never by its hex: hexes are display data and are not unique, so
          // using them as a key let one colour silently resolve to another's variant.
          selectedColorIndex: {},
          parentProductId: null,
          parentVariantId: null,
          letterCase: 'original',
          loading: false,
          additionalProducts: [],
          selectedAdditionalProducts: new Set(),
          characterPrice: 0,
          moneyFormat: '{{amount}}',
          validationError: false,
          
          init() {
            const element = this.$el;
            const configAttr = element.getAttribute('data-config');
            if (configAttr) {
              try {
                const parsedConfig = JSON.parse(configAttr);
                this.maxLength = parsedConfig.maxLength || 20;
                this.minLength = parsedConfig.minLength || 1;
                this.availableColors = parsedConfig.colors || [];
                this.parentProductId = parsedConfig.parentProductId ?? null;
                this.parentVariantId = parsedConfig.parentVariantId || null;
                this.letterCase = parsedConfig.letterCase || 'original';
                this.additionalProducts = parsedConfig.additionalProducts || [];
                this.characterPrice = parsedConfig.characterPrice || 0;
                this.moneyFormat = parsedConfig.moneyFormat || '{{amount}}';
              } catch (e) {
                console.error('Failed to parse color builder config:', e);
              }
            } else if (config) {
              this.maxLength = config.maxLength || 20;
              this.minLength = config.minLength || 1;
              this.availableColors = config.colors || [];
              this.parentProductId = config.parentProductId ?? null;
              this.parentVariantId = config.parentVariantId || null;
              this.letterCase = config.letterCase || 'original';
            }
          },

        buildBundleSnapshotFromColoredData(coloredData) {
          let totalCents = 0;
          const components = [];
          coloredData.forEach((item) => {
            // MFS-479: variant + price come from the swatch itself. getColoredText() only returns
            // letters whose swatch resolved, so there is no silent-skip path here any more.
            if (!item.variantId) return;
            const unitCents = Number(item.price) || 0;
            totalCents += unitCents;
            components.push({
              variant_id: colorBuilderVariantGid(item.variantId),
              quantity: 1,
              price: colorBuilderMoneyDecimalFromCents(unitCents),
            });
          });
          return {
            bundlePrice: colorBuilderMoneyDecimalFromCents(totalCents),
            bundleComponents: JSON.stringify(components),
          };
        },

        openPopup() {
          this.showPopup = true;
          this.step = 1;
          this.text = '';
          this.selectedColorIndex = {};
          this.selectedAdditionalProducts.clear();
          this.validationError = false;
          document.body.style.overflow = 'hidden';
        },

        closePopup() {
          this.showPopup = false;
          this.step = 1;
          this.text = '';
          this.selectedColorIndex = {};
          this.selectedAdditionalProducts.clear();
          this.validationError = false;
          document.body.style.overflow = '';
        },

        validateInput(text) {
          // Allow letters and numbers (no spaces or punctuation) — one block per character.
          return /^[a-zA-Z0-9]*$/.test(text);
        },

        filterInput(text) {
          // Strip anything that isn't a letter or number.
          return text.replace(/[^a-zA-Z0-9]/g, '');
        },

        nextStep() {
          if (this.step === 1) {
            const trimmedText = this.text.trim();
            const textLength = trimmedText.length;
            
            if (textLength === 0) {
              return;
            }
            
            // Validate input - only letters and numbers allowed
            if (!this.validateInput(trimmedText)) {
              this.validationError = true;
              return;
            }
            
            if (textLength < this.minLength) {
              return;
            }
            
            // MFS-479: with no usable colours there is nothing to pick, and step 2 would render an
            // empty picker while still letting the customer confirm — which would add the £0 parent
            // line with no letter blocks under it. Refuse to advance.
            //
            // Deliberately silent: the trigger button in color-builder-popup.liquid applies the
            // same rule, so reaching this means the two disagreed and the popup should never have
            // opened. `validationError` is NOT reused here — it renders the "letters and numbers
            // only" copy, which would be a lie, and @input recomputes it on the next keystroke so
            // it would vanish anyway.
            if (this.availableColors.length === 0) {
              console.error('Color builder: no colours are configured for this product.');
              return;
            }

            // Clear validation error if validation passes
            this.validationError = false;

            // Transform text based on case setting before moving to step 2
            this.text = this.transformText(trimmedText);
            this.step = 2;
            // Default every character to the first swatch
            const characters = this.text.split('');
            characters.forEach((character, index) => {
              if (this.selectedColorIndex[index] == null) {
                this.selectedColorIndex[index] = 0;
              }
            });
          } else if (this.step === 2) {
            // Move to step 3 (additional products) if available
            if (this.additionalProducts.length > 0) {
              this.step = 3;
            }
          }
        },

        prevStep() {
          if (this.step === 3) {
            this.step = 2;
          } else if (this.step === 2) {
            this.step = 1;
          }
        },

        transformText(text) {
          if (this.letterCase === 'uppercase') {
            return text.toUpperCase();
          } else if (this.letterCase === 'lowercase') {
            return text.toLowerCase();
          }
          return text;
        },

        selectColor(characterIndex, colorIndex) {
          if (this.availableColors[colorIndex]) {
            this.selectedColorIndex[characterIndex] = colorIndex;
          }
        },

        // MFS-479: single place that turns a character position into its chosen swatch.
        selectedColorFor(characterIndex) {
          const index = this.selectedColorIndex[characterIndex];
          return this.availableColors[index] || this.availableColors[0] || null;
        },

        toggleAdditionalProduct(index) {
          if (this.selectedAdditionalProducts.has(index)) {
            this.selectedAdditionalProducts.delete(index);
          } else {
            this.selectedAdditionalProducts.add(index);
          }
        },

        isAdditionalProductSelected(index) {
          return this.selectedAdditionalProducts.has(index);
        },

        calculateTotalCost() {
          // MFS-479: sum each letter's own swatch price. Colours can be priced differently (the
          // old hex-keyed price map allowed it) but this total ignored that and multiplied a flat
          // characterPrice; it agreed with the cart only while every colour cost the same.
          const characters = this.text.trim().split('');
          let total = characters.reduce((sum, character, index) => {
            const swatch = this.selectedColorFor(index);
            const unit = swatch && swatch.price != null ? Number(swatch.price) : Number(this.characterPrice) || 0;
            return sum + (Number.isFinite(unit) ? unit : 0);
          }, 0);

          // Add selected additional products prices
          this.selectedAdditionalProducts.forEach((index) => {
            const product = this.additionalProducts[index];
            if (product && product.price) {
              total += product.price;
            }
          });
          
          return total;
        },

        formatMoney(cents) {
          // Try theme's helper first
          if (window.Alpine && Alpine.store('xHelper') && Alpine.store('xHelper').formatMoney) {
            return Alpine.store('xHelper').formatMoney(cents, this.moneyFormat);
          }
          // Try Shopify's formatMoney
          if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
            return window.Shopify.formatMoney(cents, this.moneyFormat);
          }
          // Fallback formatting
          const dollars = (cents / 100).toFixed(2);
          return `$${dollars}`;
        },

        getCharacterColor(characterIndex) {
          const swatch = this.selectedColorFor(characterIndex);
          return swatch ? swatch.value : '#000000';
        },

        isColorSelected(characterIndex, colorIndex) {
          return this.selectedColorIndex[characterIndex] === colorIndex;
        },

        // MFS-479: carries the resolved variant + price, so callers never look a colour up by hex.
        // Letters whose swatch didn't resolve are dropped here rather than deep in the cart build,
        // and confirmBuilder() refuses to submit when that leaves the list short.
        getColoredText() {
          const characters = this.text.trim().split('');
          return characters
            .map((character, index) => {
              const swatch = this.selectedColorFor(index);
              if (!swatch || !swatch.variantId) return null;
              return {
                character,
                color: swatch.value,
                variantId: swatch.variantId,
                price: swatch.price != null ? Number(swatch.price) : Number(this.characterPrice) || 0,
              };
            })
            .filter(Boolean);
        },

        async confirmBuilder() {
          if (this.loading) return;
          
          this.loading = true;
          const coloredData = this.getColoredText();
          // Text is already transformed in nextStep(), just trim it
          const text = this.text.trim();

          try {
            if (!this.parentVariantId) {
              throw new Error('Parent product variant ID is missing');
            }
            if (this.parentProductId == null) {
              throw new Error('Parent product ID is missing');
            }
            // MFS-479: every letter must have resolved to a block. getColoredText() drops any that
            // didn't, so a short list means we'd be about to add a bundle with letters missing —
            // exactly the failure this ticket exists for. Refuse rather than ship a partial name.
            if (coloredData.length !== text.length) {
              throw new Error('Some letters could not be matched to a block. Please try again.');
            }

            // Build character data for parent product
            const characterData = [];
            coloredData.forEach((item, index) => {
              characterData.push({
                char: item.character,
                color: item.color,
                pos: index + 1
              });
            });

            const bundleGroupId =
              typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `cb_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
            const bundleSnapshot = this.buildBundleSnapshotFromColoredData(coloredData);
            
            // Build items array with parent and nested children
            // MFS-373: do NOT set `_bundle_id` on the parent line. The migrated Building Blocks
            // product carries an `engine.bundle_cart_transform` metafield, so the Engine cart
            // transform expands any line that has both `_bundle_id` and `_bundle_components`.
            // Here we add the coloured letter blocks as their own child lines (correct £ price +
            // per-letter `Character`), so letting the transform ALSO expand the parent would
            // double the components. Omitting `_bundle_id` keeps the transform dormant.
            const items = [
              {
                id: this.parentVariantId,
                quantity: 1,
                properties: {
                  'Name': text,
                  '_builder_characters': JSON.stringify(characterData),
                  _bundle_price: bundleSnapshot.bundlePrice,
                  _bundle_components: bundleSnapshot.bundleComponents,
                  _bundle_group: bundleGroupId,
                }
              }
            ];
            
            // Add child products with parent_id pointing to parent variant
            coloredData.forEach((item) => {
              items.push({
                id: parseInt(item.variantId, 10),
                quantity: 1,
                parent_id: this.parentVariantId,
                properties: {
                  '_is_color_builder_child': 'true',
                  'Character': item.character,
                  // MFS-373: engraving in Engine's personalisation wire-format. Engine's
                  // OrderPersonalisationMappingService reads `_text_line_1` (→ TEXT_LINE_1) and
                  // maps it to this letter-block product's `text_line_1` text line. The block's
                  // colour is the component product itself, so it needs no personalisation attr.
                  '_text_line_1': item.character,
                  _bundle_group: bundleGroupId,
                }
              });
            });
            
            // Add selected additional products as nested items
            this.selectedAdditionalProducts.forEach((index) => {
              const product = this.additionalProducts[index];
              if (product && product.variantId) {
                items.push({
                  id: parseInt(product.variantId, 10),
                  quantity: 1,
                  parent_id: this.parentVariantId,
                  properties: {
                    '_is_color_builder_additional': 'true',
                    _bundle_group: bundleGroupId,
                  }
                });
              }
            });
            
            // Add all items (parent + nested children + additional products) in a single request
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                items: items,
                sections: window.Alpine && Alpine.store('xCartHelper') 
                  ? Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
                  : [],
                sections_url: window.location.pathname
              })
            });
            
            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.description || error.message || 'Failed to add product to cart');
            }
            
            const data = await response.json();
            
            if (data.sections && window.Alpine && Alpine.store('xCartHelper')) {
              Alpine.store('xCartHelper').swapSections(data.sections);

              const cartIcon = document.querySelector('#cart-icon-bubble span');
              if (cartIcon) {
                Alpine.store('xCartHelper').currentItemCount = parseInt(cartIcon.innerHTML);
              }
            }
            
            // Dispatch cart update events
            document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            
            // Dispatch success event
            document.dispatchEvent(new CustomEvent('color-builder:confirmed', {
              detail: {
                text: text,
                coloredData: coloredData,
                cartData: data
              }
            }));
            
            this.closePopup();
            
            // Show cart notification or open mini cart
            if (window.Alpine && Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
              Alpine.store('xCartNoti').setItem(data);
            } else if (window.Alpine && Alpine.store('xMiniCart')) {
              Alpine.store('xMiniCart').openCart();
            }
          } catch (error) {
            console.error('Error adding to cart:', error);
            alert(error.message || 'Failed to add product to cart. Please try again.');
          } finally {
            this.loading = false;
          }
        }
      }});
    });
  });
}
